import React from 'react'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Switch from '@mui/material/Switch'
import { ISODateTimePicker } from '../util/ISOPickers'
import { TimeZoneSelect } from '../selection'
import { UserSelect } from '../selection'
import { ScheduleSelect } from '../selection'
import { ScheduleSelectionFormValue } from './ScheduleSelectionCreateDialog'

interface ScheduleSelectionFormProps {
  value: ScheduleSelectionFormValue
  onChange: (val: ScheduleSelectionFormValue) => void
}

export default function ScheduleSelectionForm(
  props: ScheduleSelectionFormProps,
): React.JSX.Element {
  const { value, onChange } = props

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label='Name'
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <FormLabel>Participants (min. 2)</FormLabel>
          <UserSelect
            multiple
            value={value.userIDs}
            onChange={(userIDs: string[]) => onChange({ ...value, userIDs })}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TimeZoneSelect
          value={value.timeZone}
          onChange={(timeZone: string) => onChange({ ...value, timeZone })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <ISODateTimePicker
          label='Start Date/Time'
          required
          fullWidth
          value={value.startTime}
          onChange={(startTime: string) => onChange({ ...value, startTime })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          required
          type='number'
          label='Rotation Length (days)'
          value={value.rotationLengthDays}
          onChange={(e) =>
            onChange({
              ...value,
              rotationLengthDays: parseInt(e.target.value, 10) || 1,
            })
          }
          inputProps={{ min: 1, max: 365 }}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          required
          type='number'
          label='Number of Rounds'
          value={value.numRounds}
          onChange={(e) =>
            onChange({
              ...value,
              numRounds: parseInt(e.target.value, 10) || 1,
            })
          }
          inputProps={{ min: 1, max: 100 }}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          type='number'
          label='Pick Deadline'
          value={value.pickDeadlineMinutes}
          onChange={(e) =>
            onChange({
              ...value,
              pickDeadlineMinutes: parseInt(e.target.value, 10) || 1440,
            })
          }
          inputProps={{ min: 1 }}
          helperText='Minutes allowed for each pick (e.g. 1440 = 24 hours)'
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          select
          label='Timeout Action'
          value={value.timeoutAction}
          onChange={(e) =>
            onChange({
              ...value,
              timeoutAction: e.target.value as 'auto_assign' | 'skip',
            })
          }
          helperText='What happens if a user misses their pick deadline'
        >
          <MenuItem value='auto_assign'>Auto-assign earliest block</MenuItem>
          <MenuItem value='skip'>Skip user</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <FormLabel>Target Schedule (optional)</FormLabel>
          <ScheduleSelect
            value={value.targetScheduleID}
            onChange={(targetScheduleID: string) =>
              onChange({ ...value, targetScheduleID })
            }
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={value.notifySMS}
              onChange={(e) =>
                onChange({ ...value, notifySMS: e.target.checked })
              }
            />
          }
          label='Also notify via SMS when it&#39;s a user&#39;s turn to pick'
        />
      </Grid>
      {value.userIDs.length > 0 &&
        value.numRounds > 0 &&
        value.rotationLengthDays > 0 && (
          <Grid item xs={12}>
            <FormLabel>
              Summary: {value.userIDs.length * value.numRounds} total blocks (
              {value.userIDs.length} users &times; {value.numRounds} rounds),
              each {value.rotationLengthDays} day(s) long ={' '}
              {value.userIDs.length * value.numRounds * value.rotationLengthDays}{' '}
              total days of coverage
            </FormLabel>
          </Grid>
        )}
    </Grid>
  )
}
